import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@moneyskyline/ui';
import { AmountText } from '@/components/AmountText';

export interface ReadonlyAnnotationOptionLike {
  anchor_transaction_id: string;
  currency: string;
  fingerprint: string;
  summary: {
    line_item_count: number;
    has_splits: boolean;
    has_user_categories: boolean;
    has_business_annotations: boolean;
  };
  preview_line_items: readonly {
    line_item_id: string;
    amount: string;
    category_id: string | null;
    category_slug: string | null;
    is_business: boolean;
    tags: readonly string[];
    version: number;
  }[];
}

export type SubmissionState = 'idle' | 'pending' | 'error' | 'succeeded';

export interface AnnotationChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: readonly ReadonlyAnnotationOptionLike[];
  selectedAnchorTransactionId: string | null;
  consentEpoch: number;
  submissionState: SubmissionState;
  onSelectAnchor: (anchorTransactionId: string) => void;
  onConfirm: (selection: { anchor_transaction_id: string; fingerprint: string }) => void;
}

function summaryFacts(option: ReadonlyAnnotationOptionLike): string[] {
  return [
    `${option.summary.line_item_count} line items`,
    option.summary.has_splits ? 'Contains splits' : null,
    option.summary.has_user_categories ? 'User categories' : null,
    option.summary.has_business_annotations ? 'Business annotations' : null,
  ].filter((fact): fact is string => Boolean(fact));
}

function snapshotOption(option: ReadonlyAnnotationOptionLike): ReadonlyAnnotationOptionLike {
  return {
    ...option,
    summary: { ...option.summary },
    preview_line_items: option.preview_line_items.map((line) => ({ ...line, tags: [...line.tags] })),
  };
}

function sameOptionFacts(left: ReadonlyAnnotationOptionLike, right: ReadonlyAnnotationOptionLike): boolean {
  if (left.anchor_transaction_id !== right.anchor_transaction_id || left.currency !== right.currency || left.fingerprint !== right.fingerprint) return false;
  if (
    left.summary.line_item_count !== right.summary.line_item_count ||
    left.summary.has_splits !== right.summary.has_splits ||
    left.summary.has_user_categories !== right.summary.has_user_categories ||
    left.summary.has_business_annotations !== right.summary.has_business_annotations ||
    left.preview_line_items.length !== right.preview_line_items.length
  ) return false;
  return left.preview_line_items.every((line, index) => {
    const other = right.preview_line_items[index];
    return (
      line.line_item_id === other.line_item_id && line.amount === other.amount && line.category_id === other.category_id &&
      line.category_slug === other.category_slug && line.is_business === other.is_business && line.version === other.version &&
      line.tags.length === other.tags.length && line.tags.every((tag, tagIndex) => tag === other.tags[tagIndex])
    );
  });
}

export function AnnotationChoiceDialog({
  open,
  onOpenChange,
  options,
  selectedAnchorTransactionId,
  consentEpoch,
  submissionState,
  onSelectAnchor,
  onConfirm,
}: AnnotationChoiceDialogProps) {
  const [selectionSnapshot, setSelectionSnapshot] = useState<{ epoch: number; option: ReadonlyAnnotationOptionLike } | null>(null);
  const [attempt, setAttempt] = useState<{ epoch: number } | null>(null);
  const confirmingRef = useRef(false);
  const previousSubmissionStateRef = useRef(submissionState);
  const previousConsentEpochRef = useRef(consentEpoch);
  const openerRef = useRef<HTMLElement | null>(null);
  const selected = options.find((option) => option.anchor_transaction_id === selectedAnchorTransactionId) ?? null;
  const storedOption = selectionSnapshot?.option ?? null;
  const optionForSnapshot = selected;

  if (selectionSnapshot && (
    selectionSnapshot.epoch !== consentEpoch || !optionForSnapshot || !sameOptionFacts(optionForSnapshot, storedOption!)
  )) {
    setSelectionSnapshot(null);
  }

  if (attempt && (attempt.epoch !== consentEpoch || submissionState === 'error' || submissionState === 'succeeded')) {
    setAttempt(null);
  }

  const selectionIsCurrent = Boolean(selectionSnapshot && selectionSnapshot.epoch === consentEpoch && optionForSnapshot && storedOption && sameOptionFacts(optionForSnapshot, storedOption));

  useEffect(() => {
    if (previousConsentEpochRef.current !== consentEpoch || (previousSubmissionStateRef.current === 'pending' && submissionState !== 'pending')) {
      confirmingRef.current = false;
    }
    previousConsentEpochRef.current = consentEpoch;
    previousSubmissionStateRef.current = submissionState;
  }, [consentEpoch, submissionState]);

  function confirmSelection() {
    if (!selectionIsCurrent || !storedOption || submissionState !== 'idle' || confirmingRef.current) return;
    confirmingRef.current = true;
    setAttempt({ epoch: consentEpoch });
    onConfirm({ anchor_transaction_id: storedOption.anchor_transaction_id, fingerprint: storedOption.fingerprint });
  }

  function selectOption(option: ReadonlyAnnotationOptionLike) {
    const snapshot = snapshotOption(option);
    setSelectionSnapshot({ epoch: consentEpoch, option: snapshot });
    onSelectAnchor(option.anchor_transaction_id);
  }

  const submissionLocked = submissionState === 'pending' || submissionState === 'succeeded' || attempt !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto"
        aria-describedby="annotation-choice-description"
        onOpenAutoFocus={() => {
          openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          openerRef.current?.focus();
          openerRef.current = null;
        }}
      >
        <DialogHeader>
          <DialogTitle>Choose annotation history</DialogTitle>
          <DialogDescription id="annotation-choice-description">
            Choose which saved record annotations to retain for this transaction.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3" role="radiogroup" aria-labelledby="annotation-choice-label">
          <span id="annotation-choice-label" className="text-sm font-medium">Available annotation records</span>
          {options.map((option, index) => {
            const checked = Boolean(selectionIsCurrent && storedOption?.anchor_transaction_id === option.anchor_transaction_id);
            const publicLabel = `Use option ${index + 1}, ${option.currency}, ${summaryFacts(option).join(', ')}`;
            return (
              <label
                key={option.anchor_transaction_id}
                className={[
                  'flex cursor-pointer flex-col gap-2 rounded-md border p-3 text-sm',
                  checked ? 'border-2 border-primary' : 'border-input',
                ].join(' ')}
              >
                <span className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="annotation-history"
                    value={`option-${index + 1}`}
                    checked={checked}
                    aria-label={publicLabel}
                    disabled={submissionLocked}
                    onChange={() => selectOption(option)}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="font-medium">{option.currency}</span>
                    <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {summaryFacts(option).map((fact) => <span key={fact}>{fact}</span>)}
                    </span>
                  </span>
                </span>
                <ul className="ml-7 flex flex-col gap-1 text-xs text-muted-foreground">
                  {option.preview_line_items.map((line) => (
                    <li key={line.line_item_id} className="flex flex-wrap gap-x-2 gap-y-1">
                      <AmountText value={line.amount} currency={option.currency} />
                      {line.category_slug && <span>{line.category_slug}</span>}
                      {line.is_business && <span>Business</span>}
                      {line.tags.map((tag) => <span key={tag}>{tag}</span>)}
                    </li>
                  ))}
                </ul>
              </label>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" className="min-h-11" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" className="min-h-11" disabled={!selectionIsCurrent || submissionLocked} onClick={confirmSelection}>
            Confirm selection
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AnnotationChoiceDialog;
